import moment from 'moment'
import type { Card, ClassicCard, ContentCards } from '@braze/web-sdk'
import config from 'constants/config'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import { trackClientEvent } from 'analytics/analytics'
import { formatBrazeUserId } from './lib/formatBrazeUserId'
import { reportClientError } from 'services/errorReportingService'
import { isE2EUser } from 'lib/checkout/form'

let _braze: any = undefined
let _brazeInitPromise: Promise<any> | undefined = undefined

async function getBraze() {
  if (config.BRAZE_API_KEY && !_braze) {
    try {
      const brazeInit = async() => {
        // First, check if service worker is supported
        if ('serviceWorker' in navigator) {
          // Ensure document is in a safe state before accessing Service Worker APIs
          await new Promise((resolve) => {
            if (document.readyState === 'complete' || document.readyState === 'interactive') {
              resolve(undefined)
            } else {
              window.addEventListener('load', () => resolve(undefined))
            }
          })

          const existingRegistration = await navigator.serviceWorker.getRegistration()

          if (!existingRegistration) {
            // Register service worker for the first time
            await navigator.serviceWorker.register('/service-worker.js', { scope: '/' })
          } else {
            try {
              // Force check for updates to existing service worker
              await existingRegistration.update()
            } catch(error) {
              // Ignore any errors with update since it should prioritise work with what it has
              console.error('Error updating service worker', error)
            }
          }

          // Wait for the service worker to be ready
          await navigator.serviceWorker.ready
        }

        // Initialize Braze
        _braze = await import(/* webpackChunkName: "Braze-SDK" */ '@braze/web-sdk').then(brazeSdk => {
          // Something's causing people to be opted out of Braze even though they didn't do anything to be opted out.
          // We don't even offer a way to opt out of Braze. Might be adblockers? In any case, `enableSDK()` clears the opt-out cookie.
          brazeSdk.enableSDK()

          brazeSdk.initialize(config.BRAZE_API_KEY!, {
            baseUrl: 'sdk.iad-05.braze.com',
            enableLogging: false,
          })

          return brazeSdk
        })

        window.__DEBUG_BRAZE__ = _braze
        return _braze
      }

      if (!_brazeInitPromise) {
        _brazeInitPromise = brazeInit()
      }

      return _brazeInitPromise
    } catch(error) {
      console.error('Error during Braze initialization:', error)
      throw error
    }
  }

  return Promise.resolve(_braze)
}

function mapBrazeNotification(card: ClassicCard): App.UserNotification {
  const url = card.url ? addQueryParamsToPath(card.url, { utm_medium: 'notification_centre_web' }) : undefined

  return {
    id: card.id,
    created: card.created ?? undefined,
    description: card.description,
    title: card.title,
    imageUrl: card.imageUrl,
    viewed: card.viewed,
    url,
    originalCard: card,
  }
}

function filterInvalidNotifications(notifications: Array<Card>) {
  return notifications.filter(notification => {
    // Extra key value pair set in braze to filter by device/platform
    if (!notification.extras.allowed_devices) return true
    return notification.extras.allowed_devices.includes('web')
  })
}

export async function changeBrazeUser(params: App.AuthAccount, options: { isSignUp?: boolean } = {}) {
  const { memberId, ...user } = params

  const sdk = await getBraze()

  if (sdk && memberId && !isE2EUser(user.email)) {
    try {
      const brazeUser = _braze.getUser()
      const brand = config.brandConfig.subscriptionParentBrand.toLowerCase()

      sdk.changeUser(formatBrazeUserId(memberId))

      brazeUser.setFirstName(user.givenName)
      brazeUser.setLastName(user.surname)
      brazeUser.setEmail(user.email)
      brazeUser.setPhoneNumber(user.phonePrefix ? `${user.phonePrefix}${user.phone}` : user.phone)
      brazeUser.setCustomUserAttribute('postcode', user.postcode)
      brazeUser.setCustomUserAttribute('brand', brand)

      if (user.dob) {
        const dob = moment(user.dob).startOf('day')
        brazeUser.setDateOfBirth(dob.year(), dob.month() + 1, dob.date())
      }

      if (options.isSignUp) {
        brazeUser.setCustomUserAttribute('new_user', true)
        brazeUser.setCustomUserAttribute('signup_date', moment.utc().format('YYYY-MM-DDTHH:mm:ss'))
      }

      sdk.openSession()
    } catch(error) {
      console.error('Error during Braze user change:', error)
      reportClientError(error, { brand: config.BRAND, memberId, message: 'Error during Braze user change' })
    }
  }
}

export async function getUserNotifications() {
  const sdk = await getBraze()

  if (sdk) {
    return new Promise<Array<App.UserNotification>>(resolve => {
      const subscriptionId = sdk.subscribeToContentCardsUpdates((contentCards: ContentCards) => {
        const cards = filterInvalidNotifications(contentCards.cards).map(mapBrazeNotification)
        resolve(cards)

        // done with our single update, remove this subscription
        sdk.removeSubscription(subscriptionId)
      })
      sdk.requestContentCardsRefresh()
    })
  }
}

// Stores the current content cards subscription ID so we can remove it later if need be.
let currentSubscriptionId: string | undefined = undefined
export async function subscribeToUserNotifications(
  onCardUpdate: (cards: Array<App.UserNotification>) => void,
) {
  const sdk = await getBraze()

  if (sdk) {
    if (currentSubscriptionId) {
      sdk.removeSubscription(currentSubscriptionId)
      currentSubscriptionId = undefined
    }

    currentSubscriptionId = sdk.subscribeToContentCardsUpdates((contentCards: ContentCards) => {
      const cards = filterInvalidNotifications(contentCards.cards).map(mapBrazeNotification)
      onCardUpdate(cards)
    })
  }
}

export async function unsubscribeToUserNotifications() {
  const sdk = await getBraze()

  if (sdk && currentSubscriptionId) {
    sdk.removeSubscription(currentSubscriptionId)
    currentSubscriptionId = undefined
  }
}

export async function logNotificationClick(notification: App.UserNotification) {
  const sdk = await getBraze()
  if (sdk) {
    sdk.logCardClick(notification.originalCard, true)
  }
}

export async function logNotificationImpression(notification: App.UserNotification) {
  const sdk = await getBraze()
  if (sdk) {
    sdk.logCardImpressions([notification.originalCard], true)
  }
}

export async function canShowPushPermissionPrompt() {
  const sdk = await getBraze()
  if (!sdk) return false

  const isPushSupported = sdk.isPushSupported()
  const isPushPermissionGranted = sdk.isPushPermissionGranted()
  const isPushBlocked = sdk.isPushBlocked()

  return isPushSupported && !isPushPermissionGranted && !isPushBlocked
}

export async function isPushNotificationsPermissionGranted() {
  const sdk = await getBraze()
  if (!sdk) return false

  const isPushSupported = sdk.isPushSupported()
  const isPushPermissionGranted = sdk.isPushPermissionGranted()
  const isPushBlocked = sdk.isPushBlocked()

  return isPushSupported && isPushPermissionGranted && !isPushBlocked
}

export async function requestBrowserPushPermission() {
  const sdk = await getBraze()
  if (sdk) {
    sdk.requestPushPermission(
      () => {
        trackClientEvent({
          subject: 'native_permission_prompt',
          action: 'opt_in',
          category: 'web_push_notifications',
          type: 'interaction',
          optimizelyEventId: '6020180791263232',
          optimizelyEventKey: 'notification_opt_ins',
        })
        subscribeToPushNotifications()
      },
      () => {
        console.warn('Something went wrong requesting push permission')
      },
    )
  }
}

let currentPushSubscriptionId: string | undefined = undefined
export async function subscribeToPushNotifications() {
  const sdk = await getBraze()

  if (sdk) {
    const isPushSupported = sdk.isPushSupported()
    const isPushPermissionGranted = sdk.isPushPermissionGranted()
    const isPushBlocked = sdk.isPushBlocked()

    if (!isPushSupported || isPushBlocked || !isPushPermissionGranted) {
      console.warn('Push notifications are blocked in this browser')
      return false
    }

    if (currentPushSubscriptionId) {
      sdk.removeSubscription(currentPushSubscriptionId)
      currentPushSubscriptionId = undefined
    }

    // Register browser for push notifications after permission is granted
    sdk.requestPushPermission()

    // TODO: Find the right type for inAppMessage
    currentPushSubscriptionId = sdk.subscribeToInAppMessage((inAppMessage: any) => {
      sdk.showInAppMessage(inAppMessage)
    })
  }
}

export async function unsubscribeToPushNotifications() {
  const sdk = await getBraze()

  if (sdk && currentPushSubscriptionId) {
    sdk.removeSubscription(currentPushSubscriptionId)
    currentPushSubscriptionId = undefined
  }
}
